import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProviders, useIntegrations, useDeleteIntegration } from '../../hooks/useIntegrations';
import { useIntegrationSubscriptions } from '../../hooks/useIntegrationSubscriptions';
import { useGoogleConnections, useGoogleWatches } from '../../hooks/useGoogleWatches';
import {
  Badge,
  Button,
  ConfirmModal,
  ProviderLogo,
  Table,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  getStatusVariant,
} from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import { useSpaceId } from '../../contexts/SpaceContext';
import { routes } from '../../lib/routes';
import { getProviderFallback } from '../../lib/provider-instructions';
import {
  getHintEntries,
  highestSeverity,
  severityToBadgeVariant,
} from '../../lib/integration-hints';
import type { Integration, Provider, Subscription } from '../../types/integrations';
import { formatRelativeTime } from '../activity/utils';
import { ExternalLink } from 'lucide-react';

export default function IntegrationList() {
  const navigate = useNavigate();
  const spaceId = useSpaceId();
  const { success, error: showError } = useToast();
  const { data: providers, isLoading: providersLoading } = useProviders();
  const { data: integrations, isLoading: integrationsLoading } = useIntegrations();
  const { data: subscriptions, isLoading: subscriptionsLoading } =
    useIntegrationSubscriptions(spaceId);
  const { data: googleConnections, isLoading: googleLoading } = useGoogleConnections();
  const deleteIntegration = useDeleteIntegration();

  // Google connection rows show org-wide counters by default; override them with
  // SPACE-scoped stats summed from this space's watches (each watch routes to
  // one space), so the integrations list matches the per-space connect page.
  const gmailConn = (googleConnections ?? []).find((c) => c.provider === 'google-gmail');
  const chatConn = (googleConnections ?? []).find((c) => c.provider === 'google-chat');
  const calendarConn = (googleConnections ?? []).find((c) => c.provider === 'google-calendar');
  const gmailWatchesQ = useGoogleWatches(gmailConn?.integrationId);
  const chatWatchesQ = useGoogleWatches(chatConn?.integrationId);
  const calendarWatchesQ = useGoogleWatches(calendarConn?.integrationId);
  const googleSpaceStats = useMemo(() => {
    const map: Record<
      string,
      { eventCount: number; errorCount: number; lastEventAt?: string; lastErrorAt?: string }
    > = {};
    const add = (connId: string | undefined, ws: { eventCount?: number; errorCount?: number; lastEventAt?: string; lastErrorAt?: string }[] | undefined) => {
      if (!connId) return;
      const list = ws ?? [];
      const maxOf = (vals: (string | undefined)[]) =>
        vals.filter((v): v is string => !!v).sort().at(-1);
      map[connId] = {
        eventCount: list.reduce((s, w) => s + (w.eventCount ?? 0), 0),
        errorCount: list.reduce((s, w) => s + (w.errorCount ?? 0), 0),
        lastEventAt: maxOf(list.map((w) => w.lastEventAt)),
        lastErrorAt: maxOf(list.map((w) => w.lastErrorAt)),
      };
    };
    add(gmailConn?.integrationId, gmailWatchesQ.data);
    add(chatConn?.integrationId, chatWatchesQ.data);
    add(calendarConn?.integrationId, calendarWatchesQ.data);
    return map;
  }, [
    gmailConn?.integrationId,
    chatConn?.integrationId,
    calendarConn?.integrationId,
    gmailWatchesQ.data,
    chatWatchesQ.data,
    calendarWatchesQ.data,
  ]);

  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    integration: Integration | null;
  }>({ isOpen: false, integration: null });

  const providerByName = new Map<string, Provider>((providers ?? []).map((p) => [p.name, p]));
  const getProviderDisplayName = (name: string) =>
    providerByName.get(name)?.displayName ?? getProviderFallback(name).displayName;

  const handleDelete = async () => {
    if (!deleteModal.integration) return;

    try {
      await deleteIntegration.mutateAsync(deleteModal.integration.integrationId);
      success('Integration deleted successfully');
      setDeleteModal({ isOpen: false, integration: null });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete integration');
    }
  };

  const subscriptionColumns = [
    {
      key: 'displayName',
      header: 'Name',
      render: (item: Subscription) => {
        const providerDisplayName = getProviderDisplayName(item.provider);
        return (
          <div className="flex items-center gap-3">
            <ProviderLogo
              slug={item.provider}
              displayName={providerDisplayName}
              className="h-8 w-8"
              grayscale
            />
            <div className="min-w-0">
              <p className="font-medium text-foreground">{item.displayName}</p>
              <p className="text-xs text-muted-foreground">{providerDisplayName}</p>
            </div>
          </div>
        );
      },
    },
    {
      key: 'source',
      header: 'Source',
      render: () => (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="info" className="cursor-default">From org</Badge>
          </TooltipTrigger>
          <TooltipContent className="text-xs">
            Managed by org admins
          </TooltipContent>
        </Tooltip>
      ),
    },
    {
      key: 'eventTypeCount',
      header: 'Event types',
      render: (item: Subscription) => (
        <span className="text-muted-foreground">
          {item.eventTypeCount === 0
            ? 'all'
            : `${item.eventTypeCount} type${item.eventTypeCount === 1 ? '' : 's'}`}
        </span>
      ),
    },
    {
      key: 'ruleCount',
      header: 'Rules',
      render: (item: Subscription) => (
        <span className="text-muted-foreground">{item.ruleCount}</span>
      ),
    },
    {
      key: 'lastEventAt',
      header: 'Last event',
      render: (item: Subscription) => {
        if (!item.lastEventAt) {
          return <span className="text-muted-foreground">Never</span>;
        }
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-muted-foreground cursor-default">
                {formatRelativeTime(item.lastEventAt)}
              </span>
            </TooltipTrigger>
            <TooltipContent className="text-xs">
              {new Date(item.lastEventAt).toLocaleString()}
            </TooltipContent>
          </Tooltip>
        );
      },
    },
  ];

  const columns = [
    {
      key: 'displayName',
      header: 'Name',
      render: (item: Integration) => {
        const providerDisplayName = getProviderDisplayName(item.provider);
        const hintEntries = getHintEntries(item);
        const sev = highestSeverity(item);
        return (
          <div className="flex items-center gap-3">
            <ProviderLogo
              slug={item.provider}
              displayName={providerDisplayName}
              className="h-8 w-8"
              grayscale
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-medium text-foreground">{item.displayName}</p>
                {sev && hintEntries.length > 0 && (
                  <Badge
                    variant={severityToBadgeVariant(sev)}
                    className="flex items-center gap-1"
                  >
                    <span
                      className="inline-block h-1.5 w-1.5 rounded-full bg-current"
                      aria-hidden="true"
                    />
                    {hintEntries.length} hint{hintEntries.length > 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{providerDisplayName}</p>
            </div>
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (item: Integration) => (
        <Badge variant={getStatusVariant(item.status)}>{item.status}</Badge>
      ),
    },
    {
      key: 'eventCount',
      header: 'Events',
      render: (item: Integration) => (
        <span className="text-muted-foreground">{item.eventCount}</span>
      ),
    },
    {
      key: 'lastEventAt',
      header: 'Last event',
      render: (item: Integration) => {
        if (!item.lastEventAt) {
          return <span className="text-muted-foreground">Never</span>;
        }
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-muted-foreground cursor-default">
                {formatRelativeTime(item.lastEventAt)}
              </span>
            </TooltipTrigger>
            <TooltipContent className="text-xs">
              {new Date(item.lastEventAt).toLocaleString()}
            </TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      key: 'errorCount',
      header: 'Errors',
      render: (item: Integration) => (
        <span className={item.errorCount > 0 ? 'text-red-600 dark:text-red-400 font-medium' : 'text-muted-foreground'}>
          {item.errorCount}
        </span>
      ),
    },
    {
      key: 'lastErrorAt',
      header: 'Last error',
      render: (item: Integration) => {
        if (item.errorCount === 0) {
          return <span className="text-muted-foreground">—</span>;
        }
        if (!item.lastErrorAt) {
          return <span className="text-muted-foreground">unknown</span>;
        }
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-muted-foreground cursor-default">
                {formatRelativeTime(item.lastErrorAt)}
              </span>
            </TooltipTrigger>
            <TooltipContent className="text-xs">
              {new Date(item.lastErrorAt).toLocaleString()}
            </TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      render: (item: Integration) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteModal({ isOpen: true, integration: item });
            }}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30"
          >
            Delete
          </Button>
        </div>
      ),
      className: 'text-right',
    },
  ];

  // Google Workspace gets its own section: it's an org-managed connection with
  // a bespoke connect flow (Google sign-in + mailbox/space watches, not a
  // pasted webhook or routing rules), so neither the space-webhook table nor
  // the org-subscription table fits. Same stat columns, no delete (managed via
  // the Google connect page), rows deep-link there.
  const googleColumns = [
    {
      key: 'displayName',
      header: 'Name',
      render: (item: Integration) => (
        <div className="flex items-center gap-3">
          <ProviderLogo
            slug={item.provider}
            displayName="Google Workspace"
            className="h-8 w-8"
            grayscale
          />
          <div className="min-w-0">
            <p className="font-medium text-foreground">{item.displayName}</p>
            <p className="text-xs text-muted-foreground">Google Workspace</p>
          </div>
        </div>
      ),
    },
    columns.find((c) => c.key === 'status')!,
    {
      key: 'eventCount',
      header: 'Events',
      render: (item: Integration) => (
        <span className="text-muted-foreground">
          {googleSpaceStats[item.integrationId]?.eventCount ?? 0}
        </span>
      ),
    },
    {
      key: 'lastEventAt',
      header: 'Last Event',
      render: (item: Integration) => {
        const at = googleSpaceStats[item.integrationId]?.lastEventAt;
        if (!at) return <span className="text-muted-foreground">Never</span>;
        return (
          <Tooltip>
            <TooltipTrigger>
              <span className="text-muted-foreground">{formatRelativeTime(at)}</span>
            </TooltipTrigger>
            <TooltipContent>{new Date(at).toLocaleString()}</TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      key: 'errorCount',
      header: 'Errors',
      render: (item: Integration) => {
        const n = googleSpaceStats[item.integrationId]?.errorCount ?? 0;
        return (
          <span className={n > 0 ? 'text-red-600 dark:text-red-400 font-medium' : 'text-muted-foreground'}>
            {n}
          </span>
        );
      },
    },
    {
      key: 'lastErrorAt',
      header: 'Last Error',
      render: (item: Integration) => {
        const stats = googleSpaceStats[item.integrationId];
        if (!stats || stats.errorCount === 0) return <span className="text-muted-foreground">—</span>;
        if (!stats.lastErrorAt) return <span className="text-muted-foreground">—</span>;
        return (
          <Tooltip>
            <TooltipTrigger>
              <span className="text-muted-foreground">{formatRelativeTime(stats.lastErrorAt)}</span>
            </TooltipTrigger>
            <TooltipContent>{new Date(stats.lastErrorAt).toLocaleString()}</TooltipContent>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Integrations</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Connect external services to receive events via webhooks
        </p>
      </div>

      {/* 1. Provider catalog — start with what's available to connect. */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Providers</h2>
        {providersLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-36 rounded-lg border border-border bg-card animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(providers ?? []).map((provider) => {
              const eventCount = provider.supportedEventTypes?.length ?? 0;
              return (
                <div
                  key={provider.name}
                  className="rounded-lg border border-border bg-card p-5 flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-start gap-3 mb-2">
                      <ProviderLogo
                        slug={provider.name}
                        displayName={provider.displayName}
                        grayscale
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-foreground">{provider.displayName}</h3>
                          <Badge variant="gray">{provider.ingestionType}</Badge>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Receive {eventCount > 0 ? `${eventCount} event type${eventCount === 1 ? '' : 's'}` : 'events'}
                      {' '}from {provider.displayName} via {provider.ingestionType}.
                    </p>
                    {provider.documentationUrl && (
                      <a
                        href={provider.documentationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-500 mt-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Documentation <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  <div className="mt-4">
                    <Link to={`${routes.console.spaceIntegrationNew(spaceId)}?provider=${provider.name}`}>
                      <Button size="sm">Connect</Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 2. Google Workspace — org-managed connection with its own connect flow. */}
      {(googleLoading || (googleConnections ?? []).length > 0) && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-foreground">Google Workspace</h2>
          <Table
            columns={googleColumns}
            data={googleConnections ?? []}
            keyExtractor={(item) => item.integrationId}
            onRowClick={() => navigate(routes.console.spaceGoogle(spaceId))}
            emptyMessage="No Google Workspace connection set up for your organization yet."
            isLoading={googleLoading}
          />
        </div>
      )}

      {/* 3. Space-owned integrations — webhooks the space itself configured. */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">
            Space Configured Integrations
          </h2>
        </div>

        <Table
          columns={columns}
          data={integrations ?? []}
          keyExtractor={(item) => item.integrationId}
          onRowClick={(item) => navigate(routes.console.spaceIntegration(spaceId, item.integrationId))}
          emptyMessage="No integrations configured yet. Connect a provider above to get started."
          isLoading={integrationsLoading}
        />
      </div>

      {/* 4. Org integrations routing events into this space. */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">
          Organization Subscribed Integrations
        </h2>
        <Table
          columns={subscriptionColumns}
          data={subscriptions ?? []}
          keyExtractor={(item) => item.integrationId}
          onRowClick={(item) =>
            navigate(routes.console.spaceSubscription(spaceId, item.integrationId))
          }
          emptyMessage="No org integrations routing events into this space yet."
          isLoading={subscriptionsLoading}
        />
      </div>

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, integration: null })}
        onConfirm={handleDelete}
        title="Delete Integration"
        message={`Are you sure you want to delete "${deleteModal.integration?.displayName}"? Incoming webhooks will stop being processed. This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteIntegration.isPending}
      />
    </div>
  );
}
